/**
 * Error text a person can act on (ENG-2002).
 *
 * The admin API answers an expired session with a 403 whose body is
 * "Returned invalid token from cache" (or "Token is expired", …). Dialogs that
 * show the server's own text verbatim then tell a customer to "review the
 * values", which sends them looking for a mistake that isn't there (SUP-683).
 * Use `withAuthRetry` around the request so an expired session is renewed and
 * the request re-run; use `userFacingApiError` for whatever still fails.
 */
import { isAuthTokenError, refreshAuthToken } from "./refreshAuthToken";

export const SESSION_EXPIRED_MESSAGE =
  "Your session has expired. Reload the page and sign in again, then retry.";

/**
 * Run `request`; if it fails because the session expired, renew the session
 * once and run it again. `request` must build its API client inside the
 * function so the retry picks up the new token.
 */
export const withAuthRetry = async <T>(
  request: () => Promise<T>
): Promise<T> => {
  try {
    return await request();
  } catch (reason) {
    if (!isAuthTokenError(reason)) throw reason;
    const renewed = await refreshAuthToken();
    if (!renewed) throw reason;
    return request();
  }
};

/** The server's `error` / `detail` text, or the body when it's a string, else null. */
const serverMessage = (reason: unknown): string | null => {
  const data = (reason as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === "string") return data || null;
  if (data && typeof data === "object") {
    const { error, detail } = data as { error?: unknown; detail?: unknown };
    if (typeof error === "string" && error) return error;
    if (typeof detail === "string" && detail) return detail;
  }
  return null;
};

/**
 * Text to show for a failed request: the session-expired message for an
 * auth failure, otherwise the server's message, otherwise `fallback`.
 */
export const userFacingApiError = (
  reason: unknown,
  fallback: string | null = null
): string | null => {
  if (isAuthTokenError(reason)) return SESSION_EXPIRED_MESSAGE;
  return serverMessage(reason) ?? fallback;
};
